import React from "react";
import { useDispatch, useSelector } from "react-redux";
import ExlyLoader from "ui/modules/ExlyLoader";
import {
  EXLY_WEBPAGE_DETAILS__REQUEST_LISTINGS,
  EXLY_WEBPAGE_DETAILS__SET_SAVE_CTA,
  EXLY_WEBPAGE_DETAILS__SET_PREPEND_PLAN,
  EXLY_WEBPAGE_DETAILS__SET_LISTINGS,
  EXLY_WEBPAGE_DETAILS__SET_SAVED_REORDER_TYPE,
  EXLY_WEBPAGE_DETAILS__START_UPDATING,
  EXLY_WEBPAGE_DETAILS__STOP_PROCESSING,
} from "ui/pages/TemplatePreview/redux/actions";
import module_style from "../Style.module.css";
import { is_empty } from "utils/validations";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import dataProvider from "data/dataProvider";
import api from "data/APIs";
import { WebpageCreationContext } from "ui/pages/TemplatePreview";
import AccordianCard from "../Components/AccordianCard";
import { REORDERING } from "ui/pages/TemplatePreview/constants";
import Listing from "./Listing";
import Plan from "./Plan";
import UpdateName from "../Modals/UpdateName";
import AbsoluteLoader from "ui/modules/AbsoluteLoader";
import { useCustomizationStore } from "features/ManageWebpage/modules/Customizations/store/useCustomizationStore";
import { ReorderTabs } from "features/ManageWebpage/modules/Customizations/common/utils/Customization.constants";
import { logError } from "utils/error";

const form_data = {
  listings: "Listings",
  plans: "Plans",
};

const BasicType = () => {
  const dispatch = useDispatch();
  const listings_data = useSelector((state) => state.webpage_detail.listings);
  const processing = useSelector((state) => state.webpage_detail.processing);
  const updating = useSelector((state) => state.webpage_detail.updating);

  const { setActiveReorderTab } = useCustomizationStore();

  const { pushNotif } = React.useContext(WebpageCreationContext);

  React.useEffect(() => {
    dispatch({ type: EXLY_WEBPAGE_DETAILS__REQUEST_LISTINGS });

    setActiveReorderTab(ReorderTabs.BASIC);

    return () => setActiveReorderTab(null);
  }, []);

  React.useEffect(() => {
    dispatch({
      type: EXLY_WEBPAGE_DETAILS__SET_SAVE_CTA,
      save_cta_text: "Save changes",
      handleSaveCta: handleSave,
    });
  }, [listings_data]);

  const [edit_plan_heading, set_edit_plan_heading] = React.useState(false);

  const handleSave = async () => {
    if (is_empty(listings_data)) return;
    try {
      dispatch({ type: EXLY_WEBPAGE_DETAILS__START_UPDATING });

      let listings = listings_data.listings_data.listings.map((item) => {
        return {
          uuid: item.listing_id,
          priority: item.priority,
          heading: item.heading,
        };
      });
      if (!is_empty(listings))
        await dataProvider.custom_request(api.update_listing_priority, "POST", {
          listings: listings,
        });

      let plans = listings_data.plans_data?.plans?.map((item) => {
        return {
          uuid: item.plan_uuid,
          priority: item.priority,
          heading: item.heading,
        };
      });

      if (!is_empty(plans)) {
        await dataProvider.custom_request(api.update_plan_priority, "POST", {
          plans: plans,
        });
        await dataProvider.custom_request(api.update_prepend_plan, "POST", {
          prepend_plans: listings_data.prepend_plans,
        });
      }

      let categorise_type = REORDERING.category_type.no_categorisation;
      const update_status = await dataProvider.custom_request(
        api.update_listing_category,
        "POST",
        { categorise_type: categorise_type }
      );
      if (update_status.status === 200) {
        dispatch({
          type: EXLY_WEBPAGE_DETAILS__SET_SAVED_REORDER_TYPE,
          payload: categorise_type,
        });
        pushNotif(update_status.message);
        dispatch({ type: EXLY_WEBPAGE_DETAILS__STOP_PROCESSING });
      }
    } catch (err) {
      logError({
        error: err,
        occuredAt:
          "src/ui/pages/TemplatePreview/components/Sidebar/ListingReordering/ListingType/index.js",
        when: "saving data",
      });
      pushNotif(
        err?.body?.message ? err?.body?.message : "Something went wrong"
      );
      dispatch({ type: EXLY_WEBPAGE_DETAILS__STOP_PROCESSING });
    }
  };

  const handleOnDragEnd = (result) => {
    if (
      is_empty(result.destination) ||
      (result.destination.droppableId === result.source.droppableId &&
        result.destination.index === result.source.index)
    ) {
      return;
    }

    if (result.type === "TYPES") {
      let is_plan = result.draggableId.split("types-")[1] === form_data.plans;
      dispatch({
        type: EXLY_WEBPAGE_DETAILS__SET_PREPEND_PLAN,
        payload:
          (is_plan && result.destination.index === 0) ||
          (!is_plan && result.destination.index === 1),
      });
    }

    if (result.type === "PLANS") {
      let plans = listings_data.plans_data.plans;
      const reorderedPlans = plans.splice(result.source.index, 1);
      plans.splice(result.destination.index, 0, ...reorderedPlans);
      for (let i = 0; i < plans.length; i++) {
        let item = plans[i];
        item.priority = plans.length - i;
      }

      let output = listings_data;
      output.plans_data.plans = plans;
      dispatch({ type: EXLY_WEBPAGE_DETAILS__SET_LISTINGS, payload: output });
    }

    if (result.type === "LISTINGS") {
      let listings = listings_data.listings_data.listings;
      const reorderedListings = listings.splice(result.source.index, 1);
      listings.splice(result.destination.index, 0, ...reorderedListings);
      for (let i = 0; i < listings.length; i++) {
        let item = listings[i];
        item.priority = listings.length - i;
      }

      let output = listings_data;
      output.listings_data.listings = listings;
      dispatch({ type: EXLY_WEBPAGE_DETAILS__SET_LISTINGS, payload: output });
    }
  };

  return (
    <div className={`${module_style.container}`}>
      {updating && <AbsoluteLoader />}
      <div className={`${module_style.sub_heading} `}>
        Customize how offerings appear in your homepage
      </div>
      <div
        className={`${module_style.type_card_title} ${module_style.top_margin}`}
      >
        Basic Default Categorization: All offerings are listed in order of the
        upcoming slot date …
      </div>
      <div className="top_margin">
        {processing ||
        (is_empty(listings_data?.listings_data?.listings) &&
          is_empty(listings_data?.plans_data?.plans)) ? (
          <ExlyLoader />
        ) : (
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="types" type="TYPES" mode="standard">
              {(provided) => {
                return (
                  <div ref={provided.innerRef}>
                    {(is_empty(listings_data?.plans_data)
                      ? [form_data.listings]
                      : listings_data.prepend_plans
                      ? [form_data.plans, form_data.listings]
                      : [form_data.listings, form_data.plans]
                    ).map((item, index) => {
                      return (
                        <Draggable
                          key={`types-${item}`}
                          draggableId={`types-${item}`}
                          index={index}
                        >
                          {(provided) => (
                            <div>
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                              >
                                <AccordianCard
                                  handleEdit={() => {
                                    set_edit_plan_heading(true);
                                  }}
                                  title={
                                    item === form_data.listings
                                      ? listings_data.listings_data.title
                                      : listings_data.plans_data.title
                                  }
                                  provided={provided}
                                >
                                  {item === form_data.listings ? (
                                    <Listing id={`types-${item}`} />
                                  ) : (
                                    <Plan id={`types-${item}`} />
                                  )}
                                </AccordianCard>
                              </div>
                              {provided.placeholder}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {edit_plan_heading && (
        <UpdateName
          type={"listings"}
          showModal={(value) => set_edit_plan_heading(value)}
        />
      )}
    </div>
  );
};

export default BasicType;
